import * as React from "react";
import { format } from "date-fns";
import { de, enUS, nb, type Locale as DateFnsLocale } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

export interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  "aria-label"?: string;
  // Optional matcher to disable specific days in the calendar
  isDateDisabled?: (date: Date) => boolean;
  // Show a clear control inside the popover footer
  clearable?: boolean;
  // Show a compact clear icon next to the trigger (outside the button)
  showClearInTrigger?: boolean;
}

export function DatePicker({
  value: valueProp,
  onChange,
  placeholder,
  disabled,
  className,
  buttonClassName,
  isDateDisabled,
  clearable = false,
  showClearInTrigger = false,
  ...rest
}: DatePickerProps) {
  const { i18n, t } = useTranslation();
  const locale = React.useMemo<DateFnsLocale>(() => {
    const lang = (i18n?.language || "en").toLowerCase();
    if (lang.startsWith("de")) return de;
    if (lang.startsWith("nb")) return nb;
    return enUS;
  }, [i18n?.language]);
  const computedPlaceholder =
    placeholder ?? t("pages.placeholders.datum_auswaehlen", { defaultValue: "Pick a date" });
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<Date | null>(null);

  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp ?? null : internalValue;

  const handleSelect = (date?: Date) => {
    const next = date ?? null;
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
    if (next) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative inline-block">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[260px] justify-start text-left font-normal",
              !value && "text-muted-foreground",
              buttonClassName
            )}
            disabled={disabled}
            aria-label={rest["aria-label"] ?? computedPlaceholder}
          >
            <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            {value ? format(value, "P", { locale }) : <span>{computedPlaceholder}</span>}
          </Button>
        </PopoverTrigger>
        {showClearInTrigger && !!value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full z-10"
            aria-label={rest["aria-label"] ?? computedPlaceholder}
            onClick={() => {
              if (!isControlled) setInternalValue(null);
              onChange?.(null);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className={cn("w-auto p-0", className)} align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={value ?? undefined}
            onSelect={handleSelect}
            defaultMonth={value ?? undefined}
            locale={locale}
            disabled={isDateDisabled}
            initialFocus
          />
          {clearable && (
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!isControlled) setInternalValue(null);
                  onChange?.(null);
                  setOpen(false);
                }}
              >
                {t("common.clear", { defaultValue: "Clear" })}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
